import type { ColumnDef, Row, Table } from '@tanstack/react-table';
import { ActionIcon, Group, NumberInput, Select, Text, TextInput, Tooltip } from '@mantine/core';
import { IconCheck, IconEdit, IconTrash, IconX } from '@tabler/icons-react';
import type { IEquipoServicioAscensor } from '../../../modelos/CotizadorServicios/entidades/IEquipoServicioAscensor';
import type { IEquipoServicioEscalera } from '../../../modelos/CotizadorServicios/entidades/IEquipoServicioEscalera';
import type { IOpcionesCotizacionServicios, IOpcionSelectServicio } from '../../../modelos/CotizadorServicios/entidades/IOpcionesCotizacionServicios';
import { etiquetaOpcion, type ITablaEquiposMeta } from '../interfaces';

type Equipo = { Id: string };
const estiloInput = { input: { fontSize: '12px', padding: '4px 8px' } };
const estiloTooltip = { fontSize: '10px', padding: '3px 5px' };

const metaDe = <T,>(table: Table<T>) => table.options.meta as ITablaEquiposMeta<T>;
const enEdicion = <T extends Equipo>(row: Row<T>, table: Table<T>) => metaDe(table).filaEnEdicion === row.original.Id;
const vacio = (valor: unknown) => valor === '' || valor === 0 || valor === null || valor === undefined;

const columnaAcciones = <T extends Equipo>(): ColumnDef<T> => ({
    id: 'actions',
    header: 'Acciones',
    size: 80,
    cell: ({ row, table }) => {
        const meta = metaDe(table);
        if (meta.soloLectura) return null;
        if (enEdicion(row, table)) {
            return (
                <Group gap={4} justify="center" wrap="nowrap">
                    <Tooltip label="Guardar" withinPortal zIndex={9999} style={estiloTooltip}>
                        <ActionIcon variant="subtle" color="var(--mantine-color-primary-0)" size="sm" radius="xl" onClick={() => meta.alGuardar(row.original.Id)}>
                            <IconCheck size={18} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Cancelar" withinPortal zIndex={9999} style={estiloTooltip}>
                        <ActionIcon variant="subtle" color="#df675a" size="sm" radius="xl" onClick={meta.alCancelar}>
                            <IconX size={18} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            );
        }
        const bloqueado = meta.filaEnEdicion !== null;
        return (
            <Group gap={4} justify="center" wrap="nowrap">
                <Tooltip label={bloqueado ? 'Termina la edición actual' : 'Editar'} withinPortal zIndex={9999} style={estiloTooltip}>
                    <ActionIcon disabled={bloqueado} variant="transparent" color="var(--mantine-color-primary-0)" size="sm" radius="sm" onClick={() => meta.alIniciarEdicion(row.original.Id)}>
                        <IconEdit size={18} />
                    </ActionIcon>
                </Tooltip>
                <Tooltip label={bloqueado ? 'Termina la edición actual' : 'Eliminar'} withinPortal zIndex={9999} style={estiloTooltip}>
                    <ActionIcon disabled={bloqueado} variant="transparent" color="var(--mantine-color-primary-0)" size="sm" radius="sm" onClick={() => meta.alEliminar(row.original.Id)}>
                        <IconTrash size={18} />
                    </ActionIcon>
                </Tooltip>
            </Group>
        );
    }
});

const columnaTexto = <T extends Equipo>(campo: keyof T & string, encabezado: string): ColumnDef<T> => ({
    accessorKey: campo,
    header: encabezado,
    cell: ({ row, table }) => {
        const valor = row.original[campo] as string;
        if (!enEdicion(row, table)) return <Text size="xs">{valor || '-'}</Text>;
        const meta = metaDe(table);
        return (
            <TextInput
                size="xs"
                value={valor}
                placeholder={`${encabezado} *`}
                error={meta.intentoGuardar && !valor.trim()}
                onChange={(e) => meta.alCambiar(row.original.Id, campo, e.currentTarget.value)}
                styles={estiloInput}
            />
        );
    }
});

/** `etiquetaEdicion` cambia el placeholder según la fila (Altura o Longitud). */
const columnaNumero = <T extends Equipo>(
    campo: keyof T & string,
    encabezado: string,
    unidad?: string,
    etiquetaEdicion?: (fila: T, opciones: IOpcionesCotizacionServicios) => string
): ColumnDef<T> => ({
    accessorKey: campo,
    header: encabezado,
    cell: ({ row, table }) => {
        const valor = row.original[campo] as number;
        if (!enEdicion(row, table)) {
            return <Text size="xs">{vacio(valor) ? '-' : `${valor.toLocaleString('es-CO')}${unidad ? ` ${unidad}` : ''}`}</Text>;
        }
        const meta = metaDe(table);
        const etiqueta = etiquetaEdicion ? etiquetaEdicion(row.original, meta.opciones) : encabezado;
        return (
            <NumberInput
                size="xs"
                hideControls
                min={0}
                allowNegative={false}
                allowDecimal={false}
                thousandSeparator="."
                decimalSeparator=","
                value={valor || ''}
                placeholder={`${etiqueta} *`}
                suffix={unidad ? ` ${unidad}` : undefined}
                error={meta.intentoGuardar && vacio(valor)}
                onChange={(v) => meta.alCambiar(row.original.Id, campo, Number(v) || 0)}
                styles={estiloInput}
            />
        );
    }
});

/**
 * Columna con selector. `numerico` guarda el valor como número (capacidad, velocidad);
 * si no, guarda el value de la opción (Id del catálogo o texto).
 */
const columnaSelect = <T extends Equipo>(
    campo: keyof T & string,
    encabezado: string,
    opcionesDe: (opciones: IOpcionesCotizacionServicios) => IOpcionSelectServicio[],
    numerico = false
): ColumnDef<T> => ({
    accessorKey: campo,
    header: encabezado,
    cell: ({ row, table }) => {
        const meta = metaDe(table);
        const opciones = opcionesDe(meta.opciones);
        const valor = row.original[campo] as string | number;
        if (!enEdicion(row, table)) return <Text size="xs">{vacio(valor) ? '-' : etiquetaOpcion(opciones, valor)}</Text>;
        return (
            <Select
                size="xs"
                searchable
                data={opciones}
                value={vacio(valor) ? null : String(valor)}
                placeholder={`${encabezado} *`}
                error={meta.intentoGuardar && vacio(valor)}
                onChange={(v) => meta.alCambiar(row.original.Id, campo, numerico ? Number(v) || 0 : v ?? '')}
                comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                styles={estiloInput}
            />
        );
    }
});

export const columnasAscensores: ColumnDef<IEquipoServicioAscensor>[] = [
    columnaAcciones<IEquipoServicioAscensor>(),
    columnaSelect<IEquipoServicioAscensor>('TipoAscensor', 'Tipo', o => o.ascensores.tipos),
    columnaTexto<IEquipoServicioAscensor>('Referencia', 'Referencia'),
    columnaSelect<IEquipoServicioAscensor>('Capacidad', 'Capacidad', o => o.ascensores.capacidades, true),
    columnaSelect<IEquipoServicioAscensor>('Velocidad', 'Velocidad', o => o.ascensores.velocidades, true),
    columnaNumero<IEquipoServicioAscensor>('Paradas', 'Paradas'),
    columnaNumero<IEquipoServicioAscensor>('Cantidad', 'Cantidad'),
    columnaSelect<IEquipoServicioAscensor>('TipoUsoId', 'Tipo de uso', o => o.ascensores.usos),
    columnaSelect<IEquipoServicioAscensor>('HorarioId', 'Horario', o => o.horarios)
];

/** En escaleras el recorrido es la altura; en rampas y andenes es la longitud. */
export const etiquetaRecorrido = (escalera: IEquipoServicioEscalera, opciones: IOpcionesCotizacionServicios) => {
    const tipo = etiquetaOpcion(opciones.escaleras.tipos, escalera.TipoEquipoId);
    return !escalera.TipoEquipoId || tipo === 'Escalera' ? 'Altura' : 'Longitud';
};

export const columnasEscaleras: ColumnDef<IEquipoServicioEscalera>[] = [
    columnaAcciones<IEquipoServicioEscalera>(),
    columnaSelect<IEquipoServicioEscalera>('TipoEquipoId', 'Tipo', o => o.escaleras.tipos),
    columnaTexto<IEquipoServicioEscalera>('Referencia', 'Referencia'),
    columnaNumero<IEquipoServicioEscalera>('Recorrido', 'Altura / Longitud', 'mm', etiquetaRecorrido),
    columnaSelect<IEquipoServicioEscalera>('Velocidad', 'Velocidad', o => o.escaleras.velocidades, true),
    columnaNumero<IEquipoServicioEscalera>('Cantidad', 'Cantidad'),
    columnaSelect<IEquipoServicioEscalera>('EntornoOperativoId', 'Entorno operativo', o => o.escaleras.entornos),
    columnaSelect<IEquipoServicioEscalera>('HorarioId', 'Horario', o => o.horarios)
];
